import React, { useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import { notificationsAPI, usersAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from './NotificationProvider';
import {
  UserIcon,
  UserGroupIcon,
  GlobeAltIcon,
  PaperAirplaneIcon,
  BellIcon,
} from '@heroicons/react/24/outline';

/**
 * Admin component for sending notifications to different target audiences
 */
export default function NotificationDashboard({ trainingGroups = [] }) {
  const { user } = useAuth();
  const { showSuccess, showError } = useNotifications();
  const [targetType, setTargetType] = useState('single'); // 'single', 'group', 'global'
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [notificationData, setNotificationData] = useState({
    title: '',
    message: '',
    priority: 'normal',
    action_url: '',
  });

  // Only show for admins
  if (user?.is_admin === false) {
    return null;
  }

  // Get all users for single user targeting
  const { data: users } = useQuery('users', () => usersAPI.getUsers().catch(() => []));

  // Send notification mutation
  const sendNotificationMutation = useMutation(
    (data) => {
      switch (targetType) {
        case 'single':
          return notificationsAPI.adminSendTest(data.userId, data.title, data.message);
        case 'group':
          return notificationsAPI.sendCustomNotification({
            ...data,
            training_group_id: data.groupId,
          });
        case 'global':
          return notificationsAPI.sendGlobalAnnouncement({ ...data, send_to_all: true });
        default:
          throw new Error('Invalid target type');
      }
    },
    {
      onSuccess: (data) => {
        // Check if this is an error response (backend returns error details in success response)
        if (data.error_type) {
          showError(`${data.message} - ${data.details || 'No additional details'}`);
        } else {
          showSuccess(`Notification sent successfully: ${data.message || 'Message delivered'}`);
        }
        // Reset form
        setNotificationData({
          title: '',
          message: '',
          priority: 'normal',
          action_url: '',
        });
        setSelectedUserId('');
        setSelectedGroupId('');
      },
      onError: (error) => {
        showError(`Failed to send notification: ${error.response?.data?.detail || error.message}`);
      },
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!notificationData.title.trim() || !notificationData.message.trim()) {
      showError('Please fill in both title and message');
      return;
    }

    const targetData = {
      title: notificationData.title,
      message: notificationData.message,
      priority: notificationData.priority,
      action_url: notificationData.action_url,
    };

    switch (targetType) {
      case 'single':
        if (!selectedUserId) {
          showError('Please select a user');
          return;
        }
        targetData.userId = parseInt(selectedUserId);
        break;
      case 'group':
        if (!selectedGroupId) {
          showError('Please select a training group');
          return;
        }
        targetData.groupId = parseInt(selectedGroupId);
        break;
      case 'global':
        // No additional data needed for global announcements
        break;
    }

    sendNotificationMutation.mutate(targetData);
  };

  const getTargetDescription = () => {
    switch (targetType) {
      case 'single':
        return 'Send a notification to a specific user';
      case 'group':
        return 'Send a notification to all members of a training group';
      case 'global':
        return 'Send a notification to all users in the system';
      default:
        return '';
    }
  };

  return (
    <div className="rounded-lg bg-white p-6 shadow-md">
      <h2 className="mb-6 text-xl font-semibold text-gray-900">Send Notifications</h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Target Selection */}
        <div>
          <label className="mb-3 block text-sm font-medium text-gray-700">Target Audience</label>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <button
              type="button"
              onClick={() => setTargetType('single')}
              className={`flex items-center justify-center gap-2 rounded-lg border p-3 text-sm font-medium transition-colors ${
                targetType === 'single'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <UserIcon className="h-5 w-5" />
              Single User
            </button>
            <button
              type="button"
              onClick={() => setTargetType('group')}
              className={`flex items-center justify-center gap-2 rounded-lg border p-3 text-sm font-medium transition-colors ${
                targetType === 'group'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <UserGroupIcon className="h-5 w-5" />
              Training Group
            </button>
            <button
              type="button"
              onClick={() => setTargetType('global')}
              className={`flex items-center justify-center gap-2 rounded-lg border p-3 text-sm font-medium transition-colors ${
                targetType === 'global'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <GlobeAltIcon className="h-5 w-5" />
              Everyone
            </button>
          </div>
          <p className="mt-2 text-sm text-gray-600">{getTargetDescription()}</p>
        </div>

        {/* Target Selection Fields */}
        {targetType === 'single' && (
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Select User</label>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Choose a user...</option>
              {users?.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.first_name} {user.last_name} ({user.email})
                </option>
              ))}
            </select>
          </div>
        )}

        {targetType === 'group' && (
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Select Training Group
            </label>
            <select
              value={selectedGroupId}
              onChange={(e) => setSelectedGroupId(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Choose a training group...</option>
              {trainingGroups?.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name} ({group.user_memberships?.length || 0} members)
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Notification Content */}
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Title</label>
            <input
              type="text"
              value={notificationData.title}
              onChange={(e) => setNotificationData({ ...notificationData, title: e.target.value })}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter notification title"
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Message</label>
            <textarea
              value={notificationData.message}
              onChange={(e) =>
                setNotificationData({ ...notificationData, message: e.target.value })
              }
              rows={4}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter notification message"
              required
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Priority</label>
              <select
                value={notificationData.priority}
                onChange={(e) =>
                  setNotificationData({ ...notificationData, priority: e.target.value })
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Action URL (Optional)
              </label>
              <input
                type="text"
                value={notificationData.action_url}
                onChange={(e) =>
                  setNotificationData({ ...notificationData, action_url: e.target.value })
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="/path/to/page"
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={sendNotificationMutation.isLoading}
            className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
          >
            <PaperAirplaneIcon className="h-4 w-4" />
            {sendNotificationMutation.isLoading ? 'Sending...' : 'Send Notification'}
          </button>
        </div>
      </form>
    </div>
  );
}
